import React, { type MouseEvent } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import FlagFilledIcon from '@atlaskit/icon/glyph/flag-filled';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import ToolTip from '@atlaskit/tooltip';
// eslint-disable-next-line jira/restricted/@atlassian+jira-common-styles
import { gridSize } from '@atlassian/jira-common-styles/src';
import { useIntl } from '@atlassian/jira-intl';
import messages from '../../messages';
import {
	ADD_FLAG_BUTTON_TEST_ID,
	FLAGGED_BUTTON_TEST_ID,
	FLAG_CONTAINER_TEST_ID,
} from '../constants';

export type Props = {
	isFlagged: boolean;
	shouldRenderInlineAddFlag: boolean;
	onAddFlag: (event: MouseEvent) => void;
};

export const Flag = ({ isFlagged, shouldRenderInlineAddFlag, onAddFlag }: Props) => {
	const { formatMessage } = useIntl();

	return (
		<FlagContainer data-testid={FLAG_CONTAINER_TEST_ID}>
			<ToolTip
				content={
					isFlagged
						? formatMessage(messages.flaggedTooltip)
						: formatMessage(messages.addFlagTooltip)
				}
			>
				{isFlagged && (
					<FlagFilledIcon
						size="small"
						primaryColor={token('color.icon.danger', colors.R300)}
						label="Flagged icon"
						testId={FLAGGED_BUTTON_TEST_ID}
					/>
				)}

				{!isFlagged && shouldRenderInlineAddFlag && (
					<AddFlagButton
						aria-label="Add Flag"
						iconBefore={
							<FlagFilledIcon
								size="small"
								primaryColor={token('color.text.subtlest', colors.N200)}
								label="Add flag icon"
							/>
						}
						spacing="compact"
						testId={ADD_FLAG_BUTTON_TEST_ID}
						onClick={onAddFlag}
					/>
				)}
			</ToolTip>
		</FlagContainer>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FlagContainer = styled.div({
	position: 'relative',
	zIndex: 1 /* surface the element above the interaction layer so tooltips can be triggered */,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AddFlagButton = styled(Button)({
	opacity: '0',
	transition: 'opacity .35s ease',
	'&:hover': {
		opacity: '1',
		background: 'none',
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	margin: `-${gridSize / 2}px`,
});
